import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';
import utils from '../../universal/utils';
import adminFetch from '../../universal/adminFetch';
import useIsMountedRef from '../../hooks/useIsMountedRef';

const useStyles = makeStyles((theme) => ({
  paper: {
    padding: theme.spacing(3),
  },
  cards: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  card: {
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    border: `1px solid ${theme.palette.divider}`,
    background: theme.palette.background.default,
  },
  cardValue: {
    fontFamily: 'monospace',
    fontSize: '1.4rem',
    fontWeight: 700,
  },
  periodRow: {
    display: 'flex',
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
    flexWrap: 'wrap',
  },
  tableContainer: {
    marginTop: theme.spacing(2),
  },
}));

const PERIOD_OPTIONS = [7, 14, 30, 90];

export default function UiExperimentsAnalyticsPanel({ userId, refreshToken = 0 }) {
  const classes = useStyles();
  const isMountedRef = useIsMountedRef();
  const basePath = useMemo(() => utils.getEdgeBasePath(), []);

  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [summary, setSummary] = useState(null);
  const [events, setEvents] = useState([]);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    setError('');
    try {
      const [summaryRes, eventsRes] = await Promise.all([
        adminFetch(`${basePath}/api/v7/admin/ui-experiments/v2/summary/adpass/${userId}?days=${days}`),
        adminFetch(`${basePath}/api/v7/admin/ui-experiments/v2/events/adpass/${userId}?days=${days}&limit=50`),
      ]);
      const summaryData = await summaryRes.json();
      const eventsData = await eventsRes.json();
      if (!summaryData?.ok) throw new Error(summaryData?.error || 'Failed to load summary');
      if (!eventsData?.ok) throw new Error(eventsData?.error || 'Failed to load events');
      if (!isMountedRef.current) return;
      setSummary(summaryData.summary || null);
      setEvents(Array.isArray(eventsData.items) ? eventsData.items : []);
    } catch (e) {
      if (isMountedRef.current) {
        setError(e?.message || String(e));
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  }, [basePath, days, isMountedRef, userId]);

  useEffect(() => {
    load();
  }, [load, refreshToken]);

  return (
    <Paper className={classes.paper} elevation={1}>
      <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gridGap={16}>
        <Box display="flex" alignItems="center" gridGap={8}>
          <TrendingUpIcon color="primary" />
          <Typography variant="h6">UI Experiments Analytics</Typography>
        </Box>
        <Button variant="outlined" color="primary" onClick={load} disabled={loading}>
          Refresh
        </Button>
      </Box>

      <Box className={classes.periodRow}>
        {PERIOD_OPTIONS.map((option) => (
          <Button
            key={option}
            size="small"
            variant={days === option ? 'contained' : 'outlined'}
            color="primary"
            onClick={() => setDays(option)}
          >
            {option}d
          </Button>
        ))}
      </Box>

      {loading ? (
        <Box display="flex" justifyContent="center" marginTop={3}>
          <CircularProgress />
        </Box>
      ) : null}

      {error ? (
        <Typography color="error" style={{ marginTop: 16 }}>
          {error}
        </Typography>
      ) : null}

      {summary ? (
        <Box className={classes.cards}>
          <Paper className={classes.card} elevation={0}>
            <Typography variant="caption" color="textSecondary">Eligible</Typography>
            <Typography className={classes.cardValue}>{summary.eligibleCount}</Typography>
          </Paper>
          <Paper className={classes.card} elevation={0}>
            <Typography variant="caption" color="textSecondary">Prompt pending</Typography>
            <Typography className={classes.cardValue}>{summary.pendingCount}</Typography>
          </Paper>
          <Paper className={classes.card} elevation={0}>
            <Typography variant="caption" color="textSecondary">Accepted</Typography>
            <Typography className={classes.cardValue}>{summary.acceptedCount}</Typography>
          </Paper>
          <Paper className={classes.card} elevation={0}>
            <Typography variant="caption" color="textSecondary">Declined</Typography>
            <Typography className={classes.cardValue}>{summary.declinedCount}</Typography>
          </Paper>
          <Paper className={classes.card} elevation={0}>
            <Typography variant="caption" color="textSecondary">Switched back</Typography>
            <Typography className={classes.cardValue}>{summary.switchedBackCount}</Typography>
          </Paper>
          <Paper className={classes.card} elevation={0}>
            <Typography variant="caption" color="textSecondary">Accept rate</Typography>
            <Typography className={classes.cardValue}>{summary.acceptRate}%</Typography>
          </Paper>
        </Box>
      ) : null}

      <TableContainer className={classes.tableContainer} component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>When</TableCell>
              <TableCell>Event</TableCell>
              <TableCell>User</TableCell>
              <TableCell>Actor</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {events.map((event) => (
              <TableRow key={event._id}>
                <TableCell>
                  {event.createdAt ? new Date(event.createdAt).toLocaleString() : '—'}
                </TableCell>
                <TableCell>{event.eventType}</TableCell>
                <TableCell>{event.userId}</TableCell>
                <TableCell>{event.actorUserId || '—'}</TableCell>
              </TableRow>
            ))}
            {!events.length && !loading ? (
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography variant="body2" color="textSecondary">
                    No events in this period yet.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

UiExperimentsAnalyticsPanel.propTypes = {
  userId: PropTypes.string,
  refreshToken: PropTypes.number,
};
